import * as React from 'react';
import { Button } from '@cian/ui-kit/button';
import { clsx } from 'clsx';
import { throttle } from 'throttle-debounce';

import * as styles from './StickButtons.css';

const SCROLL_THROTTLE_DELAY = 150;

export interface IStickButtonsProps {
  headerButtonsRef: React.MutableRefObject<HTMLElement | null>;
  footerButtonsRef: React.MutableRefObject<HTMLElement | null>;
  isCallConsultantButtonHidden: boolean;
  onCallButtonClick: () => void;
  onRequestButtonClick: () => void;
}

export const StickButtons: React.FC<IStickButtonsProps> = ({
  headerButtonsRef,
  footerButtonsRef,
  isCallConsultantButtonHidden,
  onCallButtonClick,
  onRequestButtonClick,
}) => {
  const [isVisible, setVisible] = React.useState(false);

  const scrollHandler = React.useCallback(() => {
    if (!headerButtonsRef.current || !footerButtonsRef.current) {
      return;
    }

    const headerTop = headerButtonsRef.current.getBoundingClientRect().top;
    const footerTop = footerButtonsRef.current.offsetTop;

    const hasHeader = !(headerTop <= 0 && Math.abs(headerTop) >= headerButtonsRef.current.offsetHeight);
    const hasFooter = window.scrollY + window.innerHeight >= footerTop;

    // Скрываются стик кнопки, если кнопки хедера появились на экране
    if (hasHeader && isVisible) {
      setVisible(false);

      return;
    }

    // Скрывается стик кнопки, если кнопки футера появились на экране.
    if (hasFooter && isVisible) {
      setVisible(false);

      return;
    }

    // Отображаются стик кнопки, если нет кнопок хедера и футера на экране.
    if (!hasHeader && !hasFooter && !isVisible) {
      setVisible(true);

      return;
    }
  }, [isVisible, headerButtonsRef, footerButtonsRef]);

  const handleScroll = React.useCallback(throttle(SCROLL_THROTTLE_DELAY, scrollHandler), [scrollHandler]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);

      return () => window.removeEventListener('scroll', handleScroll);
    }

    return;
  }, [handleScroll]);

  return (
    <div className={clsx(styles['container'], isVisible && styles['visible'])}>
      {!isCallConsultantButtonHidden && (
        <Button size="XS" onClick={onCallButtonClick} fullWidth data-testid="ContactsStickButton">
          Позвонить
        </Button>
      )}
      <Button
        size="XS"
        onClick={onRequestButtonClick}
        theme="fill_secondary"
        fullWidth
        data-testid="RequestFormStickButton"
      >
        Оставить заявку
      </Button>
    </div>
  );
};
